import React from 'react';

import { IconSpinner, LayoutComposer } from '../../components/index.js';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer.js';
import FooterContainer from '../FooterContainer/FooterContainer.js';

import { validProps } from './Field';
import SectionBuilder from './SectionBuilder/SectionBuilder.js';
import StaticPage from './StaticPage.js';

import css from './PageBuilder.module.css';

const getMetadata = (meta, schemaType, fieldOptions) => {
  const { pageTitle, pageDescription, socialSharing } = meta;
  const title = validProps(pageTitle, fieldOptions)?.content;
  const description = validProps(pageDescription, fieldOptions)?.content;
  const openGraph = validProps(socialSharing, fieldOptions);
  const schemaImage = openGraph?.images1200?.[0]?.url;
  const schemaImageMaybe = schemaImage ? { image: [schemaImage] } : {};
  const isArticle = ['Article', 'NewsArticle', 'TechArticle'].includes(schemaType);
  const schemaHeadlineMaybe = isArticle ? { headline: title } : {};

  return {
    title,
    description,
    schema: {
      '@context': 'http://schema.org',
      '@type': schemaType || 'WebPage',
      description,
      name: title,
      ...schemaHeadlineMaybe,
      ...schemaImageMaybe,
    },
    socialSharing: openGraph,
  };
};

const LoadingSpinner = () => (
  <div className={css.loading}>
    <IconSpinner delay={600} />
  </div>
);

const CONTENT_REPLACEMENTS = [
  {
    match: 'Getting beautiful',
    replacement:
      'Finding a professional makeup artist has never been easier. Search by date and preferred time to discover available Sydney artists and request your appointment in minutes.',
  },
  {
    match: 'Hotline Glam is a platform that helps clients discover available makeup artists',
    replacement:
      'Hotline Glam helps clients find available Sydney makeup artists, compare their work and prices, and send a booking request—all in one place.',
  },
  {
    match: 'Browse artist profiles, choose an artist',
    replacement:
      'Choose your date and preferred time, browse available artists, then select an artist and send a booking request.',
  },
  {
    match: 'Payment is only processed if the artist accepts your booking request',
    replacement:
      'Enter your payment details when sending a request. Payment is only processed if the artist accepts your booking.',
  },
  {
    match: 'Payments are securely processed through Stripe',
    replacement:
      'Payments are securely processed through Stripe. Hotline Glam releases the payout 48 hours after the appointment is completed. Stripe then transfers the funds to the artist’s connected bank account; normal bank processing times may apply.',
  },
];

const isDuplicateFaqIntro = item => {
  const content = JSON.stringify(item).toLowerCase();
  return (
    content.includes('frequently asked questions') ||
    content.includes('everything you need to know about using hotline glam')
  );
};

const replaceHomepageContent = value => {
  if (typeof value === 'string') {
    const replacement = CONTENT_REPLACEMENTS.find(({ match }) => value.includes(match));
    return replacement ? replacement.replacement : value;
  }

  if (Array.isArray(value)) {
    return value.filter(item => !isDuplicateFaqIntro(item)).map(replaceHomepageContent);
  }

  if (value && typeof value === 'object') {
    return Object.keys(value).reduce(
      (result, key) => ({ ...result, [key]: replaceHomepageContent(value[key]) }),
      {}
    );
  }

  return value;
};

const PageBuilder = props => {
  const {
    pageAssetsData,
    inProgress,
    error,
    fallbackPage,
    schemaType,
    options,
    currentPage,
    featuredListings,
    notice,
    ...pageProps
  } = props;

  if (!pageAssetsData && fallbackPage && !inProgress && error) {
    return fallbackPage;
  }

  const { sections: pageSections = [], meta = {} } = pageAssetsData || {};
  const sections = replaceHomepageContent(
    pageSections.filter(section => !isDuplicateFaqIntro(section))
  );
  const pageMetaProps = getMetadata(meta, schemaType, options?.fieldComponents);

  const layoutAreas = `
    topbar
    main
    footer
  `;

  return (
    <StaticPage {...pageMetaProps} {...pageProps}>
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {layoutProps => {
          const { Topbar, Main, Footer } = layoutProps;

          return (
            <>
              <Topbar as="header" className={css.topbar}>
                <TopbarContainer currentPage={currentPage} />
              </Topbar>
              <Main as="main" id="main-content" className={css.main}>
                {notice ? <div className={css.marketplaceNotice}>{notice}</div> : null}
                {sections.length === 0 && inProgress ? (
                  <LoadingSpinner />
                ) : (
                  <SectionBuilder sections={sections} options={{ ...options, featuredListings }} />
                )}
              </Main>
              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </StaticPage>
  );
};

export { LayoutComposer, StaticPage, SectionBuilder };

export default PageBuilder;
